import { useState, useEffect, useCallback } from "react";
import {
  Plus,
  Trash2,
  Save,
  Calculator,
  ChevronDown,
  ChevronUp,
} from "lucide-react";
import { showError, showSuccess } from "../../lib/toast";
import {
  server_post_data,
  licensing_config_save,
  licensing_config_get,
} from "../../serviceconnection/ServiceConnection";

// ── Outside component ─────────────────────────────────────────────────────────
const InputField = ({
  label,
  placeholder,
  type = "text",
  value,
  onChange,
  textarea,
}) => (
  <div className="space-y-1">
    <label className="text-xs font-semibold text-muted-foreground">
      {label}
    </label>
    {textarea ? (
      <textarea
        value={value ?? ""}
        onChange={onChange}
        placeholder={placeholder}
        rows={3}
        className="w-full px-3 py-2 rounded-lg border border-border bg-background text-sm text-foreground outline-none focus:border-accent resize-none"
      />
    ) : (
      <input
        type={type}
        value={value ?? ""}
        onChange={onChange}
        placeholder={placeholder}
        className="w-full px-3 py-2 rounded-lg border border-border bg-background text-sm text-foreground outline-none focus:border-accent"
      />
    )}
  </div>
);

const CollapsibleSection = ({ title, children, defaultOpen = false }) => {
  const [open, setOpen] = useState(defaultOpen);
  return (
    <div className="section-panel p-0">
      <button
        onClick={() => setOpen((p) => !p)}
        className="w-full section-panel-header flex items-center justify-between cursor-pointer hover:bg-muted/30 transition-colors"
      >
        <h3 className="section-panel-title">{title}</h3>
        {open ? (
          <ChevronUp className="w-4 h-4 text-muted-foreground" />
        ) : (
          <ChevronDown className="w-4 h-4 text-muted-foreground" />
        )}
      </button>
      {open && <div className="section-panel-body space-y-4">{children}</div>}
    </div>
  );
};

// ── Default seeds ─────────────────────────────────────────────────────────────
const DEFAULT_PRICING = {
  onpremise: { professional: 3400, limited: 1650 },
  cloud: { professional: 2800, limited: 1350 },
};
const DEFAULT_ADDONS = [
  { id: "beas", label: "Beas Manufacturing", price: 10000 },
  { id: "produmex", label: "Produmex WMS", price: 14000 },
  { id: "focuspoint", label: "FocusPoint", price: 5500 },
  { id: "salesforce", label: "Salesforce Integration", price: 8500 },
  {
    id: "analytics",
    label: "Analytics (Power BI / Tableau / SAC)",
    price: 7000,
  },
];
const DEFAULT_LICENSE_TYPES = [
  {
    name: "Professional",
    description: "Full access to all SAP Business One modules and features",
    price_range: "$2,800/user/mo",
    features: [
      "Complete ERP functionality",
      "Financial management",
      "Sales & purchasing",
      "Inventory & production",
      "Reporting & analytics",
    ],
  },
  {
    name: "Limited",
    description: "Access to specific modules with reduced functionality",
    price_range: "$1,350/user/mo",
    features: [
      "Basic reporting",
      "Inventory visibility",
      "Sales order entry",
      "CRM access",
      "Limited financials",
    ],
  },
  {
    name: "Starter Package",
    description: "Entry-level package for small businesses",
    price_range: "$1,357/user",
    features: [
      "Up to 5 users",
      "Core ERP modules",
      "Basic customization",
      "Standard support",
      "Cloud or on-premise",
    ],
  },
];

const EMPTY_CONFIG = {
  hero_badge: "Licensing Tool",
  hero_title: "SAP Business One",
  hero_title_highlight: "Licensing Calculator",
  hero_description:
    "Get an indicative SAP Business One licence cost estimate in under 2 minutes. No email required.",
  pricing_data: DEFAULT_PRICING,
  addons_data: DEFAULT_ADDONS,
  license_types: DEFAULT_LICENSE_TYPES,
  cta_heading: "Ready for a Detailed Assessment?",
  cta_description:
    "Our SAP consultants can provide a comprehensive quote including implementation, training, and ongoing support.",
  cta_btn_text: "Book a Free Consultation",
  cta_btn_link: "/contact",
  disclaimer_text:
    "This is an indicative estimate only. SAP Business One pricing is user-based and varies by deployment, region, and partner agreement. Contact ThynkProcess for a formal quote.",
};

export default function LicensingCalculator() {
  const [config, setConfig] = useState(EMPTY_CONFIG);
  const [loading, setLoading] = useState(true);
  const [saveLoading, setSaveLoading] = useState(false);

  const sc = useCallback(
    (key, val) => setConfig((p) => ({ ...p, [key]: val })),
    [],
  );

  useEffect(() => {
    const load = async () => {
      try {
        const fd = new FormData();
        const res = await server_post_data(licensing_config_get, fd);
        const d = res.data?.data;
        if (d) setConfig({ ...EMPTY_CONFIG, ...d });
      } catch {
        showError("Failed to load config");
      } finally {
        setLoading(false);
      }
    };
    load();
  }, []);

  const handleSave = async () => {
    try {
      setSaveLoading(true);
      const fd = new FormData();
      const scalars = [
        "hero_badge",
        "hero_title",
        "hero_title_highlight",
        "hero_description",
        "cta_heading",
        "cta_description",
        "cta_btn_text",
        "cta_btn_link",
        "disclaimer_text",
      ];
      scalars.forEach((k) => fd.append(k, config[k] ?? ""));
      fd.append("pricing_data", JSON.stringify(config.pricing_data));
      fd.append("addons_data", JSON.stringify(config.addons_data));
      fd.append("license_types", JSON.stringify(config.license_types));

      const res = await server_post_data(licensing_config_save, fd);
      if (res.data?.error) {
        showError(res.data?.message);
        return;
      }
      showSuccess("Config saved!");
    } catch {
      showError("Something went wrong");
    } finally {
      setSaveLoading(false);
    }
  };

  // ── Pricing (single price per deployment/user-type) ─────────────────────────
  const setPricing = (deployment, userType, val) => {
    setConfig((p) => ({
      ...p,
      pricing_data: {
        ...p.pricing_data,
        [deployment]: {
          ...p.pricing_data[deployment],
          [userType]: Number(val) || 0,
        },
      },
    }));
  };

  // ── Add-on helpers (single price) ────────────────────────────────────────────
  const setAddon = (idx, field, val) =>
    setConfig((p) => {
      const arr = [...p.addons_data];
      arr[idx] = {
        ...arr[idx],
        [field]: field === "label" || field === "id" ? val : Number(val) || 0,
      };
      return { ...p, addons_data: arr };
    });
  const addAddon = () =>
    setConfig((p) => ({
      ...p,
      addons_data: [
        ...p.addons_data,
        { id: `addon_${Date.now()}`, label: "", price: 0 },
      ],
    }));
  const removeAddon = (idx) =>
    setConfig((p) => ({
      ...p,
      addons_data: p.addons_data.filter((_, i) => i !== idx),
    }));

  // ── License type helpers ───────────────────────────────────────────────────
  const setLicType = (idx, field, val) =>
    setConfig((p) => {
      const arr = [...p.license_types];
      arr[idx] = { ...arr[idx], [field]: val };
      return { ...p, license_types: arr };
    });
  const setLicFeature = (licIdx, featIdx, val) =>
    setConfig((p) => {
      const arr = [...p.license_types];
      const feats = [...arr[licIdx].features];
      feats[featIdx] = val;
      arr[licIdx] = { ...arr[licIdx], features: feats };
      return { ...p, license_types: arr };
    });
  const addLicFeature = (licIdx) =>
    setConfig((p) => {
      const arr = [...p.license_types];
      arr[licIdx] = { ...arr[licIdx], features: [...arr[licIdx].features, ""] };
      return { ...p, license_types: arr };
    });
  const removeLicFeature = (licIdx, featIdx) =>
    setConfig((p) => {
      const arr = [...p.license_types];
      arr[licIdx] = {
        ...arr[licIdx],
        features: arr[licIdx].features.filter((_, i) => i !== featIdx),
      };
      return { ...p, license_types: arr };
    });
  const addLicType = () =>
    setConfig((p) => ({
      ...p,
      license_types: [
        ...p.license_types,
        { name: "", description: "", price_range: "", features: [] },
      ],
    }));
  const removeLicType = (idx) =>
    setConfig((p) => ({
      ...p,
      license_types: p.license_types.filter((_, i) => i !== idx),
    }));

  if (loading)
    return (
      <div className="flex items-center justify-center h-64 text-muted-foreground text-sm">
        Loading...
      </div>
    );

  const pd = config.pricing_data;

  return (
    <div className="max-w-[1000px] mx-auto space-y-5">
      <div className="page-header">
        <div>
          <h1 className="page-title">SAP Licensing Calculator</h1>
          <p className="page-subtitle">
            Manage pricing, add-ons and licence type cards shown on the website
          </p>
        </div>
        <button
          onClick={handleSave}
          disabled={saveLoading}
          className="flex items-center gap-2 text-sm font-semibold px-4 py-2 rounded-lg text-white disabled:opacity-60"
          style={{ background: "hsl(var(--accent))" }}
        >
          <Save className="w-4 h-4" />
          {saveLoading ? "Saving..." : "Save Changes"}
        </button>
      </div>

      <CollapsibleSection title="Hero Section" defaultOpen>
        <div className="grid grid-cols-1 sm:grid-cols-2 gap-4">
          <InputField
            label="Badge Text"
            placeholder="Licensing Tool"
            value={config.hero_badge}
            onChange={(e) => sc("hero_badge", e.target.value)}
          />
          <InputField
            label="Title"
            placeholder="SAP Business One"
            value={config.hero_title}
            onChange={(e) => sc("hero_title", e.target.value)}
          />
        </div>
        <InputField
          label="Highlighted Word"
          placeholder="Licensing Calculator"
          value={config.hero_title_highlight}
          onChange={(e) => sc("hero_title_highlight", e.target.value)}
        />
        <InputField
          label="Description"
          textarea
          placeholder="Subtitle text..."
          value={config.hero_description}
          onChange={(e) => sc("hero_description", e.target.value)}
        />
      </CollapsibleSection>

      {/* ── Pricing: single price per deployment/user-type, On-Premise default ── */}
      <CollapsibleSection title="Pricing (AUD / user / month)" defaultOpen>
        {[
          ["onpremise", "On-Premise (Default)"],
          ["cloud", "Cloud"],
        ].map(([dep, depLabel]) => (
          <div key={dep}>
            <p className="text-xs font-semibold text-muted-foreground uppercase tracking-wide mb-3">
              {depLabel}
            </p>
            <div className="grid grid-cols-1 sm:grid-cols-2 gap-4">
              {[
                ["professional", "Professional User"],
                ["limited", "Limited User"],
              ].map(([type, typeLabel]) => (
                <div
                  key={type}
                  className="p-4 rounded-lg border border-border bg-muted/10 space-y-3"
                >
                  <p className="text-sm font-semibold text-foreground">
                    {typeLabel}
                  </p>
                  <InputField
                    label="Price (AUD)"
                    type="number"
                    value={pd?.[dep]?.[type] ?? ""}
                    onChange={(e) => setPricing(dep, type, e.target.value)}
                  />
                </div>
              ))}
            </div>
          </div>
        ))}
      </CollapsibleSection>

      {/* ── Add-Ons ── */}
      <CollapsibleSection title="Add-On Licences">
        <div className="space-y-3">
          {config.addons_data.map((addon, idx) => (
            <div
              key={idx}
              className="grid grid-cols-[1fr_120px_36px] gap-3 items-end p-3 rounded-lg border border-border bg-muted/10"
            >
              <InputField
                label={idx === 0 ? "Label" : ""}
                placeholder="Add-on name"
                value={addon.label}
                onChange={(e) => setAddon(idx, "label", e.target.value)}
              />
              <InputField
                label={idx === 0 ? "Price (AUD)" : ""}
                type="number"
                value={addon.price}
                onChange={(e) => setAddon(idx, "price", e.target.value)}
              />
              <button
                onClick={() => removeAddon(idx)}
                className="w-9 h-9 rounded-lg flex items-center justify-center hover:bg-muted mb-0.5"
                style={{ color: "hsl(var(--destructive))" }}
              >
                <Trash2 className="w-4 h-4" />
              </button>
            </div>
          ))}
          <button
            onClick={addAddon}
            className="flex items-center gap-2 px-3 py-2 rounded-lg border border-dashed border-border text-sm text-muted-foreground hover:text-foreground hover:border-accent transition-colors"
          >
            <Plus className="w-4 h-4" /> Add Add-On
          </button>
        </div>
      </CollapsibleSection>

      {/* ── Licence Type Cards ── */}
      <CollapsibleSection title="Licence Type Cards">
        <div className="space-y-4">
          {config.license_types.map((lt, licIdx) => (
            <div
              key={licIdx}
              className="p-4 rounded-lg border border-border bg-muted/10 space-y-3"
            >
              <div className="flex items-center justify-between">
                <p className="text-sm font-semibold text-foreground">
                  {lt.name || `Licence Type ${licIdx + 1}`}
                </p>
                <button
                  onClick={() => removeLicType(licIdx)}
                  className="w-7 h-7 rounded flex items-center justify-center hover:bg-muted"
                  style={{ color: "hsl(var(--destructive))" }}
                >
                  <Trash2 className="w-3.5 h-3.5" />
                </button>
              </div>
              <div className="grid grid-cols-1 sm:grid-cols-2 gap-3">
                <InputField
                  label="Name"
                  placeholder="Professional"
                  value={lt.name}
                  onChange={(e) => setLicType(licIdx, "name", e.target.value)}
                />
                <InputField
                  label="Price Label"
                  placeholder="$2,800/user/mo"
                  value={lt.price_range}
                  onChange={(e) =>
                    setLicType(licIdx, "price_range", e.target.value)
                  }
                />
              </div>
              <InputField
                label="Description"
                textarea
                placeholder="Brief description..."
                value={lt.description}
                onChange={(e) =>
                  setLicType(licIdx, "description", e.target.value)
                }
              />
              <div className="space-y-2">
                <p className="text-xs font-semibold text-muted-foreground">
                  Features
                </p>
                {lt.features.map((feat, featIdx) => (
                  <div key={featIdx} className="flex items-center gap-2">
                    <input
                      value={feat}
                      onChange={(e) =>
                        setLicFeature(licIdx, featIdx, e.target.value)
                      }
                      placeholder={`Feature ${featIdx + 1}`}
                      className="flex-1 px-3 py-1.5 rounded-lg border border-border bg-background text-sm text-foreground outline-none focus:border-accent"
                    />
                    <button
                      onClick={() => removeLicFeature(licIdx, featIdx)}
                      className="w-7 h-7 rounded flex items-center justify-center hover:bg-muted flex-shrink-0"
                      style={{ color: "hsl(var(--destructive))" }}
                    >
                      <Trash2 className="w-3.5 h-3.5" />
                    </button>
                  </div>
                ))}
                <button
                  onClick={() => addLicFeature(licIdx)}
                  className="flex items-center gap-1.5 text-xs text-muted-foreground hover:text-foreground transition-colors"
                >
                  <Plus className="w-3.5 h-3.5" /> Add Feature
                </button>
              </div>
            </div>
          ))}
          <button
            onClick={addLicType}
            className="flex items-center gap-2 px-3 py-2 rounded-lg border border-dashed border-border text-sm text-muted-foreground hover:text-foreground hover:border-accent transition-colors"
          >
            <Plus className="w-4 h-4" /> Add Licence Type
          </button>
        </div>
      </CollapsibleSection>

      <CollapsibleSection title="Disclaimer & CTA">
        <InputField
          label="Disclaimer Text"
          textarea
          value={config.disclaimer_text}
          onChange={(e) => sc("disclaimer_text", e.target.value)}
        />
        <div className="grid grid-cols-1 sm:grid-cols-2 gap-4 pt-2">
          <InputField
            label="CTA Heading"
            value={config.cta_heading}
            onChange={(e) => sc("cta_heading", e.target.value)}
          />
          <InputField
            label="CTA Button Text"
            value={config.cta_btn_text}
            onChange={(e) => sc("cta_btn_text", e.target.value)}
          />
        </div>
        <InputField
          label="CTA Description"
          textarea
          value={config.cta_description}
          onChange={(e) => sc("cta_description", e.target.value)}
        />
        <InputField
          label="CTA Button Link"
          placeholder="/contact"
          value={config.cta_btn_link}
          onChange={(e) => sc("cta_btn_link", e.target.value)}
        />
      </CollapsibleSection>

      <div className="flex justify-end pb-6">
        <button
          onClick={handleSave}
          disabled={saveLoading}
          className="flex items-center gap-2 px-5 py-2 rounded-lg text-sm font-semibold text-white disabled:opacity-60"
          style={{ background: "hsl(var(--accent))" }}
        >
          <Save className="w-4 h-4" />
          {saveLoading ? "Saving..." : "Save All Changes"}
        </button>
      </div>
    </div>
  );
}